import { usePictureInPictureModeContext } from '@/context/InvoiceReports';
import {
  ActiveFileType,
  TAB_KEYS,
  usePaymentDetail,
} from '@/components/InvoiceReportDetailDrawer/context/PaymentDetailContext';
import { useMfFileId } from '@/components/InvoiceReportDetailDrawer/hooks/useDataChangeReset';
import classnames from 'classnames/bind';
import { FC, memo, useMemo } from 'react';
import { EmptyPreview, MainPreview } from './Preview';
import { InvoicePreview } from './Preview/InvoicePreview';
import styles from './styles.module.scss';

const cx = classnames.bind(styles);

type DocumentPreviewTabProps = {
  pdfViewerContainerRef: React.RefObject<HTMLDivElement>;
  handleOpenPictureInPicture: () => void;
  handleUpdatePictureInPictureContent: () => void;
  reportId: string;
};

export const DocumentPreviewTab: FC<DocumentPreviewTabProps> = memo(
  ({
    reportId,
    pdfViewerContainerRef,
    handleOpenPictureInPicture,
    handleUpdatePictureInPictureContent,
  }) => {
    const { invoiceFileId, isPIPMode } = usePictureInPictureModeContext();
    const { activeTab, activeFile } = usePaymentDetail();
    const mfFileId = useMfFileId();

    const isInvoiceTab = useMemo(() => {
      return (
        activeTab === TAB_KEYS.ATTACHED_INVOICE &&
        activeFile &&
        activeFile.type === ActiveFileType.INVOICE
      );
    }, [activeTab, activeFile]);

    const invoiceFileIdForPreview = useMemo(() => {
      return isInvoiceTab && mfFileId ? mfFileId : invoiceFileId;
    }, [mfFileId, isInvoiceTab, invoiceFileId]);

    return (
      <>
        {!mfFileId && (
          <div className={cx({ [styles['hidden']]: mfFileId })}>
            <EmptyPreview />
          </div>
        )}
        {invoiceFileIdForPreview && (isInvoiceTab || isPIPMode) && (
          <div className={cx({ [styles['hidden']]: !isInvoiceTab })}>
            <InvoicePreview
              pdfViewerContainerRef={pdfViewerContainerRef}
              handleOpenPictureInPicture={handleOpenPictureInPicture}
              invoiceFileId={invoiceFileIdForPreview}
              reportId={reportId}
              handleUpdatePictureInPictureContent={
                handleUpdatePictureInPictureContent
              }
            />
          </div>
        )}
        {mfFileId && !isInvoiceTab && <MainPreview mfFileId={mfFileId} />}
      </>
    );
  }
);

DocumentPreviewTab.displayName = 'DocumentPreviewTab';
